import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { PrintMailStatusBanner } from 'pages/Servicing/PrintMail/StatusBanner'
import {
  IMailPurposeConfig,
  IPrintMailConfig,
  IPrintMailStatus,
  mailMoney,
} from 'pages/Servicing/PrintMail/types'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { getPrintMailConfig, savePrintMailConfig } from 'services'
import { Button } from 'stories/components'
import { RenderInput } from 'utils/RenderInput'

import {
  defaultMailPostage,
  defaultMailRateCard,
  defaultPrintMailConfig,
  postageInputs,
  rateCardInputs,
  settingInputs,
} from './constants'
import { PurposesTable } from './PurposesTable'

/**
 * Admin Tools > Configuration > Print & Mail.
 *
 * Whether this company posts physical mail, from where, how, and what it costs them.
 *
 * `enabled` ships off and is a separate decision from the provider credential: having a key saved
 * is not the same as being willing to spend postage out of it, and the two are usually made by
 * different people. The status band at the top is the thing to read first - a tenant posting real
 * mail while believing they are in a sandbox is the worst failure this feature has.
 */
export const PrintMailConfig = () => {
  const [isLoading, setLoading] = useState(false)
  const [isChanged, setChanged] = useState(false)
  const [config, setConfig] = useState<IPrintMailConfig>(defaultPrintMailConfig())
  const [status, setStatus] = useState<IPrintMailStatus | null>(null)
  const [providers, setProviders] = useState<Record<string, string>>({ lob: 'Lob' })
  const [returnAddress, setReturnAddress] = useState<any>(null)

  const load = async () => {
    setLoading(true)
    try {
      const res: any = await getPrintMailConfig()
      setConfig({ ...defaultPrintMailConfig(), ...(res?.config || {}) })
      setStatus(res?.status || null)
      setReturnAddress(res?.returnAddress || null)
      if (Array.isArray(res?.providers) && res.providers.length) {
        setProviders(
          Object.fromEntries(res.providers.map((name: string) => [name, name.charAt(0).toUpperCase() + name.slice(1)])),
        )
      }
      setChanged(false)
    } catch (err: any) {
      toast(err?.message || 'Could not read the print and mail configuration.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const inputs = useMemo(() => {
    const fields = settingInputs(providers)
    Object.keys(fields).forEach((key) => {
      if (fields[key].inputType === 'section') return
      fields[key].value = (config as any)[key]
    })
    return fields
  }, [config, providers])

  const postage = useMemo(() => {
    const fields = postageInputs()
    Object.keys(fields).forEach((key) => {
      fields[key].value = (config.postage as any)?.[key]
    })
    // An extra service on Standard post is a request the provider rejects outright, and the
    // backend drops it rather than passing it on - so it is not offered here either.
    fields.extraService.disabled = config.postage?.mailType === 'usps_standard'
    fields.perforatedPage.visible = config.postage?.returnEnvelope === true
    return fields
  }, [config.postage])

  const rateCard = useMemo(() => {
    const fields = rateCardInputs()
    Object.keys(fields).forEach((key) => {
      fields[key].value = (config.rateCard as any)?.[key]
    })
    return fields
  }, [config.rateCard])

  const onChangeSetting = (key: string, value: any) => {
    const next = cloneDeep(config) as any
    next[key] = value
    setConfig(next)
    setChanged(true)
  }

  const onChangeNested = (group: 'postage' | 'rateCard') => (key: string, value: any) => {
    const next = cloneDeep(config) as any
    next[group] = { ...next[group], [key]: value }
    setConfig(next)
    setChanged(true)
  }

  const onChangePurposes = (purposes: Record<string, IMailPurposeConfig>) => {
    setConfig({ ...config, purposes })
    setChanged(true)
  }

  const onSubmit = async () => {
    setLoading(true)
    try {
      const data: IPrintMailConfig = {
        ...config,
        postage: { ...defaultMailPostage(), ...(config.postage || {}) },
        rateCard: { ...defaultMailRateCard(), ...(config.rateCard || {}) },
        dispatchBatchSize: Math.min(Math.max(Number(config.dispatchBatchSize) || 25, 1), 200),
        dailyPieceCap: Math.max(Number(config.dailyPieceCap) || 0, 0),
      }
      await savePrintMailConfig(data)
      toast('Print & Mail configuration has been saved.', { type: 'info' })
    } catch (err: any) {
      toast(err?.message || 'The configuration was not saved.', { type: 'error' })
    } finally {
      setLoading(false)
    }
    // Reload so the status band and the readiness list are the server's answer, not ours.
    await load()
  }

  /** What one ordinary letter would cost at the current defaults, worked out the way the API does. */
  const examplePieceCents = useMemo(() => {
    const card = { ...defaultMailRateCard(), ...(config.rateCard || {}) }
    const post = { ...defaultMailPostage(), ...(config.postage || {}) }
    let cents = Number(card.basePieceCents) || 0
    if (post.color) cents += Number(card.colorPerPageCents) || 0
    if (post.returnEnvelope) cents += Number(card.returnEnvelopeCents) || 0
    if (post.extraService === 'certified') cents += Number(card.certifiedCents) || 0
    else if (post.extraService === 'certified_return_receipt') cents += Number(card.certifiedReturnReceiptCents) || 0
    else if (post.extraService === 'registered') cents += Number(card.registeredCents) || 0
    return Math.max(Math.round(cents), 0)
  }, [config.postage, config.rateCard])

  const usingFooterAddress = !config.fromAddressLine1?.trim()

  return (
    <div className="relative">
      <LayoutLoading show={isLoading} />
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />

      <PrintMailStatusBanner status={status} className="mb-4" />

      <p className="text-sm text-gray-600 mb-6">
        Letters are queued, not sent: queueing one writes a row and costs nothing, and a dispatcher submits it to the
        provider afterwards. That is what makes a portfolio-wide mailing reviewable before any postage is spent. The
        queue itself is under Servicing &gt; Print &amp; Mail.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {Object.keys(inputs).map((key) => {
          const input = inputs[key]
          if (input.visible === false) return null
          return (
            <div className={`input md:col-span-${input.span || 1}`} key={key}>
              <RenderInput input={input} Key={key} onChange={(_: string, value: any) => onChangeSetting(key, value)} />
            </div>
          )
        })}
      </div>

      {usingFooterAddress && (
        <p className="text-sm text-gray-600 -mt-4 mb-8">
          {returnAddress ? (
            <>
              No return address is set here, so envelopes come back to the company contact block:{' '}
              <span className="font-medium">
                {[returnAddress.name, returnAddress.addressLine1, returnAddress.city, returnAddress.state, returnAddress.zip]
                  .filter(Boolean)
                  .join(', ')}
              </span>
              .
            </>
          ) : (
            <span className="text-amber-800">
              No return address is set here and the company contact block under Configuration &gt; Footer is not
              complete, so nothing can be posted at all. Fill in either one.
            </span>
          )}
        </p>
      )}

      <h3 className="text-lg font-bold mb-1">Default postage</h3>
      <p className="text-sm text-gray-600 mb-3">
        How a letter goes out when its purpose has not said otherwise. Changing these does not touch pieces already
        waiting in the queue - each one carries the postage it was queued with.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {Object.keys(postage).map((key) => {
          const input = postage[key]
          if (input.visible === false) return null
          return (
            <div className="input" key={key}>
              <RenderInput input={input} Key={key} onChange={onChangeNested('postage')} />
            </div>
          )
        })}
      </div>

      <div className="mb-8">
        <PurposesTable
          purposes={config.purposes || {}}
          defaults={{ ...defaultMailPostage(), ...(config.postage || {}) }}
          onChange={onChangePurposes}
        />
      </div>

      <h3 className="text-lg font-bold mb-1">Rate card</h3>
      <p className="text-sm text-gray-600 mb-3">
        What this company pays per piece, in cents. Every cost the platform shows is worked out from these and is an{' '}
        <span className="font-medium">estimate</span>: no provider returns a price when a letter is created, so the
        only authority on what was charged is their invoice. The figures ship as the provider&apos;s published
        lowest-volume prices - replace them with your negotiated ones.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
        {Object.keys(rateCard).map((key) => (
          <div className="input" key={key}>
            <RenderInput input={rateCard[key]} Key={key} onChange={onChangeNested('rateCard')} />
          </div>
        ))}
      </div>
      <p className="text-sm text-gray-700 mb-8">
        One page at the defaults above: <span className="font-semibold">{mailMoney(examplePieceCents)}</span> per
        piece, estimated. Each page beyond the first adds {mailMoney(config.rateCard?.perPageCents)}.
      </p>

      <Button disabled={!isChanged} onClick={onSubmit}>
        Save
      </Button>
    </div>
  )
}
